import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { VoiceChat } from '../components/VoiceChat'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import { GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/TheMind.css'
import '../styles/UniversalGameRoom.css'

type GamePhase = 'select' | 'setup' | 'lobby' | 'playing' | 'levelComplete' | 'gameOver'
type GameMode = 'select' | 'local' | 'network'

interface Player {
  id: number
  name: string
  hand: number[]
}

export default function TheMind() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')

  // Player info for network mode
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)

  // Network multiplayer
  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const {
    session,
    createSession,
    joinSession,
    voice,
    sendMove,
    onMessage,
  } = enhancedSession

  // Game state
  const [numPlayers, setNumPlayers] = useState(2)
  const [players, setPlayers] = useState<Player[]>([])
  const [currentLevel, setCurrentLevel] = useState(1)
  const [playedCards, setPlayedCards] = useState<number[]>([])
  const [lives, setLives] = useState(3)
  const [stars, setStars] = useState(1)
  const [showingMistake, setShowingMistake] = useState(false)
  const [gameOverReason, setGameOverReason] = useState('')

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Initialize game
  const startGame = () => {
    const effectiveNumPlayers = gameMode === 'network' ? 2 : numPlayers

    const newPlayers: Player[] = Array.from({ length: effectiveNumPlayers }, (_, i) => ({
      id: i + 1,
      name: gameMode === 'network' && session
        ? (i === 0 ? session.player1_name : session.player2_name) || `Player ${i + 1}`
        : `Player ${i + 1}`,
      hand: [],
    }))

    setPlayers(newPlayers)
    setCurrentLevel(1)
    const initialLives = effectiveNumPlayers === 2 ? 2 : 3
    setLives(initialLives)
    setStars(1)
    setPlayedCards([])
    dealCards(newPlayers, 1)
    setPhase('playing')

    // Send initial state for network mode (host only)
    if (gameMode === 'network' && playerNumber === 1) {
      sendMove({ type: 'game_state', state: {
        players: newPlayers.map(p => ({ ...p, hand: [] })), // Hide hands initially
        currentLevel: 1,
        lives: initialLives,
        stars: 1,
        playedCards: [],
        phase: 'playing',
      }})
    }
  }

  // Deal cards for current level
  const dealCards = (playerList: Player[], level: number) => {
    const deck = Array.from({ length: 100 }, (_, i) => i + 1)
    const shuffled = deck.sort(() => Math.random() - 0.5)

    const updatedPlayers = playerList.map((player, idx) => ({
      ...player,
      hand: shuffled.slice(idx * level, (idx + 1) * level).sort((a, b) => a - b),
    }))

    setPlayers(updatedPlayers)
    setPlayedCards([])
  }

  // Play a card
  const playCard = (playerId: number, card: number) => {
    if (showingMistake) return

    const lastPlayedCard = playedCards[playedCards.length - 1] || 0

    // Check if this is a mistake
    const allRemainingCards = players.flatMap(p => p.hand).filter(c => c !== card)
    const lowerCards = allRemainingCards.filter(c => c < card && c > lastPlayedCard)

    if (lowerCards.length > 0) {
      // Mistake! Someone has a lower card
      setShowingMistake(true)
      const newLives = lives - 1
      setLives(newLives)

      setTimeout(() => {
        setShowingMistake(false)

        if (newLives <= 0) {
          const reason = `Game Over! You reached Level ${currentLevel}`
          setGameOverReason(reason)
          setPhase('gameOver')

          // Send game over state
          if (gameMode === 'network') {
            const updatedPlayers = players.map(p =>
              p.id === playerId ? { ...p, hand: p.hand.filter(c => c !== card) } : p
            )
            sendMove({ type: 'game_state', state: {
              players: updatedPlayers,
              playedCards: [...playedCards, card],
              lives: newLives,
              stars,
              currentLevel,
              showingMistake: false,
              phase: 'gameOver',
              gameOverReason: reason,
            }})
          }
        } else {
          // Continue with penalty
          const updatedPlayers = players.map(p =>
            p.id === playerId ? { ...p, hand: p.hand.filter(c => c !== card) } : p
          )
          setPlayers(updatedPlayers)
          const newPlayedCards = [...playedCards, card]
          setPlayedCards(newPlayedCards)

          // Send state update
          if (gameMode === 'network') {
            sendMove({ type: 'game_state', state: {
              players: updatedPlayers,
              playedCards: newPlayedCards,
              lives: newLives,
              stars,
              currentLevel,
              showingMistake: false,
              phase: 'playing',
            }})
          }
        }
      }, 2000)
    } else {
      // Correct play!
      const updatedPlayers = players.map(p =>
        p.id === playerId ? { ...p, hand: p.hand.filter(c => c !== card) } : p
      )
      setPlayers(updatedPlayers)
      const newPlayedCards = [...playedCards, card]
      setPlayedCards(newPlayedCards)

      // Check if level complete
      const totalCardsLeft = updatedPlayers.reduce((sum, p) => sum + p.hand.length, 0)
      if (totalCardsLeft === 0) {
        setTimeout(() => {
          setPhase('levelComplete')

          // Send level complete state
          if (gameMode === 'network') {
            sendMove({ type: 'game_state', state: {
              players: updatedPlayers,
              playedCards: newPlayedCards,
              lives,
              stars,
              currentLevel,
              showingMistake: false,
              phase: 'levelComplete',
            }})
          }
        }, 500)
      } else {
        // Send state update
        if (gameMode === 'network') {
          sendMove({ type: 'game_state', state: {
            players: updatedPlayers,
            playedCards: newPlayedCards,
            lives,
            stars,
            currentLevel,
            showingMistake: false,
            phase: 'playing',
          }})
        }
      }
    }
  }

  // Use a star (skip ahead)
  const useStar = () => {
    if (stars <= 0 || showingMistake) return

    const allCards = players.flatMap(p => p.hand)
    const lowestCard = Math.min(...allCards)

    // Remove lowest card from whoever has it
    const updatedPlayers = players.map(p => ({
      ...p,
      hand: p.hand.filter(c => c !== lowestCard),
    }))

    setPlayers(updatedPlayers)
    const newPlayedCards = [...playedCards, lowestCard]
    setPlayedCards(newPlayedCards)
    const newStars = stars - 1
    setStars(newStars)

    // Check if level complete
    const totalCardsLeft = updatedPlayers.reduce((sum, p) => sum + p.hand.length, 0)
    if (totalCardsLeft === 0) {
      setTimeout(() => {
        setPhase('levelComplete')

        // Send level complete state
        if (gameMode === 'network') {
          sendMove({ type: 'game_state', state: {
            players: updatedPlayers,
            playedCards: newPlayedCards,
            lives,
            stars: newStars,
            currentLevel,
            showingMistake: false,
            phase: 'levelComplete',
          }})
        }
      }, 500)
    } else {
      // Send state update
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players: updatedPlayers,
          playedCards: newPlayedCards,
          lives,
          stars: newStars,
          currentLevel,
          showingMistake: false,
          phase: 'playing',
        }})
      }
    }
  }

  // Next level (host only in network mode)
  const nextLevel = () => {
    // In network mode, only host can advance
    if (gameMode === 'network' && playerNumber !== 1) return

    const newLevel = currentLevel + 1

    if (newLevel > 12) {
      const reason = 'Victory! You beat all 12 levels!'
      setGameOverReason(reason)
      setPhase('gameOver')

      // Send victory state
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          players,
          playedCards,
          lives,
          stars,
          currentLevel,
          showingMistake: false,
          phase: 'gameOver',
          gameOverReason: reason,
        }})
      }
      return
    }

    // Gain a star every 3 levels (3, 6, 9, 12)
    const newStars = newLevel % 3 === 0 ? stars + 1 : stars
    setStars(newStars)

    setCurrentLevel(newLevel)
    dealCards(players, newLevel)
    setPhase('playing')

    // Send next level state
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        players,
        currentLevel: newLevel,
        lives,
        stars: newStars,
        playedCards: [],
        showingMistake: false,
        phase: 'playing',
      }})
    }
  }

  // Restart game
  const restartGame = () => {
    if (gameMode === 'network') {
      setPhase('lobby')
    } else {
      setPhase('setup')
    }
  }

  // Handler for sidebar message sending (network mode)
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode !== 'network') return

    // Send chat message via session
    await sendMove({ type: 'chat', message })

    // Add to local messages
    const newMessage: Message = {
      sender: 'you',
      content: message,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [gameMode, sendMove])

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      const state = data.state
      if (state.players) setPlayers(state.players)
      if (state.currentLevel !== undefined) setCurrentLevel(state.currentLevel)
      if (state.playedCards) setPlayedCards(state.playedCards)
      if (state.lives !== undefined) setLives(state.lives)
      if (state.stars !== undefined) setStars(state.stars)
      if (state.showingMistake !== undefined) setShowingMistake(state.showingMistake)
      if (state.phase) setPhase(state.phase)
      if (state.gameOverReason) setGameOverReason(state.gameOverReason)
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      // Add chat message to messages feed
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribe()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Auto-join if code provided
  useEffect(() => {
    if (autoJoinCode && phase === 'select') {
      setGameMode('network')
      setPhase('setup')
      setPlayerNumber(2)
    }
  }, [autoJoinCode, phase])

  // Check if both players joined (network mode)
  useEffect(() => {
    if (gameMode === 'network' && phase === 'lobby' && session?.player2_name) {
      sounds.playerJoined()
    }
  }, [session?.player2_name, phase, gameMode])

  // Mode selection screen
  if (phase === 'select') {
    return (
      <div className="mind-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="mind-setup">
          <div className="mind-logo">🧠</div>
          <h1 className="mind-title">The Mind</h1>
          <p className="mind-subtitle">
            A cooperative card game where you play cards in ascending order... without talking
          </p>

          <div className="mind-setup-card">
            <h2>Select Game Mode</h2>
            <div className="mode-buttons">
              <button
                className="mode-button mode-local"
                onClick={() => { setGameMode('local'); setPhase('setup'); }}
              >
                <div className="mode-icon">👥</div>
                <h3>Local Game</h3>
                <p>Play with friends on this device</p>
              </button>

              <button
                className="mode-button mode-network"
                onClick={() => { setGameMode('network'); setPhase('setup'); }}
              >
                <div className="mode-icon">🌐</div>
                <h3>Online</h3>
                <p>Play with anyone, anywhere</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Setup phase
  if (phase === 'setup') {
    // Network mode setup
    if (gameMode === 'network') {
      const handleCreateSession = async () => {
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const session = await createSession('themind', playerName)
        if (session) {
          setPlayerNumber(1)
          setPhase('lobby')
          sounds.success()
        }
      }

      const handleJoinSession = async () => {
        const code = autoJoinCode || prompt('Enter game code:')
        if (!code) return
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const success = await joinSession(code, playerName)
        if (success) {
          setPlayerNumber(2)
          setPhase('lobby')
          sounds.success()
        }
      }

      return (
        <div className="mind-container">
          <button onClick={() => setPhase('select')} className="back-button">
            ← Back
          </button>

          <div className="mind-setup">
            <div className="mind-logo">🧠</div>
            <h1 className="mind-title">The Mind</h1>
            <p className="mind-subtitle">Online Multiplayer</p>

            <div className="mind-setup-card">
              <div className="network-setup">
                <label>Your Name:</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter your name"
                  className="name-input"
                />

                <div className="network-buttons">
                  <button onClick={handleCreateSession} className="create-button">
                    Create Game
                  </button>
                  <button onClick={handleJoinSession} className="join-button">
                    Join Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Local mode setup
    return (
      <div className="mind-container">
        <button onClick={() => setPhase('select')} className="back-button">
          ← Back
        </button>

        <div className="mind-setup">
          <div className="mind-logo">🧠</div>
          <h1 className="mind-title">The Mind</h1>
          <p className="mind-subtitle">
            A cooperative card game where you play cards in ascending order... without talking
          </p>

          <div className="mind-setup-card">
            <h2>How to Play</h2>
            <div className="rules-list">
              <div className="rule-item">
                <span className="rule-icon">🎴</span>
                <span>Each player gets cards numbered 1-100</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🤐</span>
                <span>Play cards in ascending order WITHOUT communicating</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">📊</span>
                <span>Each level gives you more cards (Level 1 = 1 card, Level 2 = 2 cards...)</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">❤️</span>
                <span>Lose a life if someone plays out of order</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">⭐</span>
                <span>Use stars to discard lowest card</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🏆</span>
                <span>Beat all 12 levels to win!</span>
              </div>
            </div>

            <div className="player-selector">
              <label>Number of Players:</label>
              <div className="player-buttons">
                {[2, 3, 4].map(num => (
                  <button
                    key={num}
                    className={`player-btn ${numPlayers === num ? 'selected' : ''}`}
                    onClick={() => setNumPlayers(num)}
                  >
                    {num} Players
                  </button>
                ))}
              </div>
            </div>

            <button onClick={startGame} className="start-button">
              Start Game
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Lobby phase (network mode)
  if (phase === 'lobby') {
    const shareUrl = `${window.location.origin}${window.location.pathname}?join=${session?.session_code}`
    const copyShareLink = () => {
      navigator.clipboard.writeText(shareUrl)
      alert('Link copied to clipboard!')
    }

    const bothPlayersReady = session?.player1_name && session?.player2_name

    return (
      <div className="mind-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="mind-setup">
          <div className="mind-logo">🧠</div>
          <h1 className="mind-title">Game Lobby</h1>

          <div className="mind-setup-card">
            <div className="lobby-content">
              <div className="session-code">
                <label>Game Code:</label>
                <div className="code-display">{session?.session_code}</div>
                <button onClick={copyShareLink} className="copy-button">
                  Copy Share Link
                </button>
              </div>

              <div className="players-list">
                <h3>Players:</h3>
                <div className="player-item">
                  <span className="player-status">✓</span>
                  <span>{session?.player1_name || 'Player 1'}</span>
                </div>
                <div className="player-item">
                  <span className="player-status">{session?.player2_name ? '✓' : '⏳'}</span>
                  <span>{session?.player2_name || 'Waiting for player...'}</span>
                </div>
              </div>

              <VoiceChat
                connectionStatus={voice.connectionStatus}
                voiceStatus={{
                  localMuted: voice.isMuted,
                  remoteMuted: false,
                  localConnected: voice.isConnected,
                  remoteConnected: voice.isConnected
                }}
                playerName={playerName}
                opponentName={session?.player2_name || undefined}
                onConnect={voice.connect}
                onToggleMute={voice.toggleMute}
              />

              {bothPlayersReady && playerNumber === 1 && (
                <button onClick={startGame} className="start-button">
                  Start Game
                </button>
              )}

              {bothPlayersReady && playerNumber === 2 && (
                <div className="waiting-message">
                  Waiting for host to start the game...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (phase === 'levelComplete') {
    return (
      <div className="mind-container">
        <div className="level-complete-screen">
          <div className="complete-icon">✨</div>
          <h1>Level {currentLevel} Complete!</h1>
          <p className="complete-message">
            {currentLevel === 12 ? 'Final level done! Click Next to win!' : 'Great sync! Ready for the next challenge?'}
          </p>

          <div className="stats-row">
            <div className="stat-box">
              <span className="stat-label">Lives</span>
              <span className="stat-value">❤️ {lives}</span>
            </div>
            <div className="stat-box">
              <span className="stat-label">Stars</span>
              <span className="stat-value">⭐ {stars}</span>
            </div>
          </div>

          <button onClick={nextLevel} className="next-level-button">
            {currentLevel === 12 ? 'Finish Game' : `Continue to Level ${currentLevel + 1}`}
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'gameOver') {
    return (
      <div className="mind-container">
        <div className="game-over-screen">
          <div className="gameover-icon">
            {gameOverReason.includes('Victory') ? '🎉' : '💔'}
          </div>
          <h1>{gameOverReason.includes('Victory') ? 'Victory!' : 'Game Over'}</h1>
          <p className="gameover-message">{gameOverReason}</p>

          <div className="final-stats">
            <div className="final-stat">
              <span className="stat-label">Final Level</span>
              <span className="stat-value">{currentLevel}</span>
            </div>
            <div className="final-stat">
              <span className="stat-label">Cards Played</span>
              <span className="stat-value">{playedCards.length}</span>
            </div>
          </div>

          <div className="gameover-buttons">
            <button onClick={restartGame} className="restart-button">
              Play Again
            </button>
            <button onClick={() => navigate('/')} className="home-button">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Playing phase
  return (
    <div className="mind-container universal-game-room">
      <div className="mind-header">
        <button onClick={restartGame} className="back-button">
          ← Restart
        </button>

        {gameMode === 'network' && (
          <div className="voice-chat-container">
            <VoiceChat
              connectionStatus={voice.connectionStatus}
              voiceStatus={{
                localMuted: voice.isMuted,
                remoteMuted: false,
                localConnected: voice.isConnected,
                remoteConnected: voice.isConnected
              }}
              playerName={playerName}
              opponentName={playerNumber === 1 ? session?.player2_name : session?.player1_name}
              onConnect={voice.connect}
              onToggleMute={voice.toggleMute}
            />
          </div>
        )}

        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Level</span>
            <span className="stat-value level-badge">{currentLevel}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Lives</span>
            <span className="stat-value">{'❤️'.repeat(lives)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Stars</span>
            <span className="stat-value">{'⭐'.repeat(stars)}</span>
          </div>
        </div>
      </div>

      {showingMistake && (
        <div className="mistake-banner">
          ❌ Mistake! Someone had a lower card. -1 Life
        </div>
      )}

      <div className="game-room-content">
        <div className="mind-game-area">
          {/* Played Cards Area */}
          <div className="played-cards-area">
            <h3>Played Cards</h3>
            <div className="played-cards-pile">
              {playedCards.length === 0 ? (
                <div className="empty-pile">No cards played yet...</div>
              ) : (
                playedCards.map((card, idx) => (
                  <div key={idx} className="played-card" style={{ zIndex: idx }}>
                    {card}
                  </div>
                ))
              )}
            </div>
            <div className="last-played">
              {playedCards.length > 0 && (
                <span>Last: <strong>{playedCards[playedCards.length - 1]}</strong></span>
              )}
            </div>
          </div>

          {/* Star Action */}
          {stars > 0 && (
            <button
              onClick={useStar}
              className="star-button"
              disabled={showingMistake}
            >
              Use Star ⭐ (Discard Lowest)
            </button>
          )}

          {/* Player Hands */}
          <div className="players-area">
            {players.map(player => (
              <div key={player.id} className="player-section">
                <h3>{player.name}</h3>
                <div className="player-hand">
                  {player.hand.map(card => (
                    <button
                      key={card}
                      onClick={() => playCard(player.id, card)}
                      className="mind-card"
                      disabled={showingMistake}
                    >
                      {card}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Universal Sidebar for network mode */}
        {gameMode === 'network' && (
          <GameRoomSidebar
            mode="network"
            gameState={{
              phase: 'Playing',
              turn: `Level ${currentLevel}`,
              score: {
                Lives: `❤️ ${lives}`,
                Stars: `⭐ ${stars}`
              }
            }}
            messages={chatMessages}
            historyItems={historyItems}
            onSendMessage={handleSidebarMessage}
            collapsed={sidebar.collapsed}
            onToggleCollapse={sidebar.toggle}
            position={sidebar.position}
            quickReactions={true}
          />
        )}
      </div>
    </div>
  )
}
